import {
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import cx from "classnames";

import { useCombobox } from "./internal/ComboboxContext";

import { Flex } from "../Flex";
import { Text } from "../Text";
import { Spinner } from "../Spinner";

import styles from "./Combobox.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";
import listViewStyles from "../ListView/ListView.module.scss";
import { ComboboxSelectAll } from "./internal/ComboboxSelectAll";
import { useComboboxGroupContext } from "./internal/ComboboxGroupContext";

/**
 * Props for the ComboboxList component
 */
export type ComboboxListProps = ComponentPropsWithoutRef<"ul" | "div"> & {
  children?: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ComboboxListElement = function <Item = any>({
  children,
  ...rest
}: ComboboxListProps) {
  const {
    getMenuProps,
    multiple,
    items,
    hasAddNew = false,
    hasEmpty,
    loading,
    selectAll,
    listRef,
  } = useCombobox<Item>();
  const scrollSentinelRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { key: groupKey } = useComboboxGroupContext() ?? {};

  const isAGroup = !!groupKey;

  useEffect(() => {
    if (!scrollSentinelRef.current) return;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }
        });
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(scrollSentinelRef.current);
    return () => {
      intersectionObserver.disconnect();
    };
  }, [scrollSentinelRef, setIsScrolled]);

  if (isAGroup) {
    return <>{children}</>;
  }

  if (multiple) {
    return (
      <>
        <ul
          {...getMenuProps(rest as ComponentPropsWithoutRef<"ul">)}
          ref={listRef as React.MutableRefObject<HTMLUListElement | null>}
          data-anv="combobox-list"
          className={cx(styles["listview"], listViewStyles["listview"], {
            [styles.scrolled]: isScrolled,
          })}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
          }}
        >
          <div ref={scrollSentinelRef} className={styles["scroll-sentinel"]} />
          {!loading && selectAll && <ComboboxSelectAll />}
          {children}
        </ul>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner size="small" />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  } else {
    return (
      <>
        <ul
          {...getMenuProps(rest as ComponentPropsWithoutRef<"ul">)}
          ref={listRef as React.MutableRefObject<HTMLUListElement | null>}
          data-anv="combobox-list"
          className={listboxStyles["listbox"]}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
          }}
        >
          {children}
        </ul>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner size="small" />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  }
};

/**
 * ComboboxList component for rendering the list container of combobox items
 *
 * Features:
 * - Supports both single and multiple selection modes
 * - Automatically handles empty state with "No match found" message
 * - Shows loading spinner during async operations
 * - Integrates with combobox state management
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered combobox list container
 *
 * @example
 * <ComboboxList>
 *   {items.map((item, index) => (
 *     <ComboboxItem key={item.id} item={item} index={index}>
 *       {item.name}
 *     </ComboboxItem>
 *   ))}
 * </ComboboxList>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComboboxList = function <Item = any>({
  children,
  className,
  ...rest
}: ComboboxListProps) {
  return (
    <ComboboxListElement<Item> className={className} {...rest}>
      {children}
    </ComboboxListElement>
  );
};
